/**
 * LoginPage — 시뮬앱 로그인(포털과 동일 JWT 계정). 개발자 전용 도구라 단순 폼.
 * 세션 핸드오프: 메인앱이 복사한 1회용 티켓을 클립보드(또는 붙여넣기)로 받아 동일 사용자로 로그인.
 */
import { useState } from 'react'
import { Button, Card, Divider, Form, Input, Typography, message } from 'antd'
import { login, simExchange, toErrMsg } from './sim/api'
import { useLabel } from './framework/useLabel'
import LocaleSelect from './framework/LocaleSelect'

export default function LoginPage({ onLoggedIn, exchanging = false }: { onLoggedIn: () => void; exchanging?: boolean }) {
  const { t } = useLabel()
  const [loading, setLoading] = useState(false)
  const [ticketBusy, setTicketBusy] = useState(false)

  const onFinish = async (v: { loginId: string; password: string }) => {
    setLoading(true)
    try {
      await login(v.loginId.trim(), v.password)
      message.success(t('SIM_MSG_LOGIN_OK', '로그인 성공'))
      onLoggedIn()
    } catch (e) {
      message.error(t('SIM_MSG_LOGIN_FAIL', '로그인 실패') + ': ' + toErrMsg(e))
    } finally {
      setLoading(false)
    }
  }

  // 메인앱 계정으로 로그인 — 클립보드의 티켓으로 교환. 클립보드 불가 시 붙여넣기 입력.
  const loginWithTicket = async (explicit?: string) => {
    setTicketBusy(true)
    try {
      let ticket = explicit?.trim()
      if (!ticket) {
        try { ticket = (await navigator.clipboard.readText())?.trim() } catch { ticket = '' }
      }
      if (!ticket) {
        const typed = window.prompt(t('SIM_PROMPT_PASTE_TICKET', '메인앱에서 복사한 시뮬 티켓을 붙여넣으세요'))
        ticket = typed?.trim() || ''
      }
      if (!ticket) { setTicketBusy(false); return }
      await simExchange(ticket)
      message.success(t('SIM_MSG_MAINAPP_LOGIN_OK', '메인앱 계정으로 로그인되었습니다'))
      onLoggedIn()
    } catch (e) {
      message.error(t('SIM_MSG_TICKET_LOGIN_FAIL', '티켓 로그인 실패') + ': ' + toErrMsg(e))
    } finally {
      setTicketBusy(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1115', padding: 16 }}>
      <Card style={{ width: 360, borderRadius: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <LocaleSelect size="small" />
        </div>
        <Typography.Title level={4} style={{ marginTop: 0 }}>{t('SIM_LOGIN_TITLE', 'Mame 디바이스 시뮬레이터')}</Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>{t('SIM_LOGIN_SUBTITLE', '개발자 테스트 도구 · 포털 계정으로 로그인')}</Typography.Paragraph>
        <Button block loading={ticketBusy || exchanging} onClick={() => loginWithTicket()} style={{ marginBottom: 8 }}>
          {t('SIM_BTN_LOGIN_TICKET', '메인앱 계정으로 로그인 (티켓)')}
        </Button>
        <Divider plain style={{ margin: '8px 0', fontSize: 12 }}>{t('SIM_DIVIDER_OR_ID', '또는 아이디로')}</Divider>
        <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item name="loginId" label={t('SIM_LBL_LOGIN_ID', '아이디')} rules={[{ required: true, message: t('SIM_RULE_LOGIN_ID', '아이디를 입력하세요') }]}>
            <Input autoFocus autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" label={t('SIM_LBL_PASSWORD', '비밀번호')} rules={[{ required: true, message: t('SIM_RULE_PASSWORD', '비밀번호를 입력하세요') }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading}>{t('SIM_BTN_LOGIN', '로그인')}</Button>
        </Form>
      </Card>
    </div>
  )
}
